import React from 'react';
import { fmtDate } from '../ResearchPage';

// This name's slice of the Chatter corpus — the sentiment the public conversation carried for it,
// matched by ticker server-side. It is a READING of commentary, not a signal: it decides nothing
// and is shown as description. Absence is never a zero — if a name was not mentioned this panel
// does not render at all (the profile hides the whole section), so a blank here always means
// "mentioned, but flat", never "we don't know".

const num = (v) => (typeof v === 'number' && isFinite(v) ? v : null);
const BULLISH = '#16a34a';
const BEARISH = '#dc2626';

const BUCKET_TITLE = {
  winners: 'Among the most favoured names', losers: 'Among the least favoured names',
  movers: 'Among the biggest movers', top_companies: 'Among the most discussed names',
};

// Signed favourability in [-1,+1], drawn from the centre outward — the same honest track Chatter
// uses, where -0.9 and +0.1 must not look like the same small quantity.
function Favor({ v }) {
  const n = num(v);
  if (n == null) return <div className="h-2 w-full rounded bg-gray-100 dark:bg-gray-800" />;
  const half = Math.min(50, Math.abs(n) * 50);
  const positive = n >= 0;
  return (
    <div className="relative h-2 w-full rounded bg-gray-100 dark:bg-gray-800">
      <div className="absolute inset-y-0 rounded"
           style={{ left: positive ? '50%' : `${50 - half}%`, width: `${Math.max(1, half)}%`,
                    background: positive ? BULLISH : BEARISH, opacity: 0.8 }} />
      <div className="absolute inset-y-0 left-1/2 w-px bg-gray-300 dark:bg-gray-600" aria-hidden="true" />
    </div>
  );
}

const Stat = ({ label, value, tone }) => (
  <div className="rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-800">
    <div className="text-[9.5px] font-medium uppercase tracking-[0.14em] text-gray-400">{label}</div>
    <div className={`mt-0.5 font-serif text-lg tabular-nums ${tone || 'text-gray-900 dark:text-gray-100'}`}>
      {value}
    </div>
  </div>
);

export default function ChatterPanel({ chatter }) {
  if (!chatter || !chatter.company) return null;
  const c = chatter.company;
  const fav = num(c.favorability);
  const mom = num(c.momentum);
  const bull = num(c.bull) || 0;
  const bear = num(c.bear) || 0;
  const neutral = num(c.neutral) || 0;

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm text-gray-600 dark:text-gray-300">
          {BUCKET_TITLE[chatter.bucket] || 'Mentioned in the conversation'}
        </span>
        {chatter.as_at
          ? <span className="text-[11px] text-gray-400">as at {fmtDate(chatter.as_at)}</span> : null}
      </div>

      <div className="mt-3">
        <div className="mb-1 flex items-center justify-between text-[11px] text-gray-400">
          <span>bearish</span><span>favourability</span><span>bullish</span>
        </div>
        <Favor v={fav} />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Favourability"
              value={fav == null ? '—' : `${fav > 0 ? '+' : ''}${fav.toFixed(2)}`}
              tone={fav == null ? 'text-gray-400'
                : fav > 0.05 ? 'text-green-600 dark:text-green-400'
                  : fav < -0.05 ? 'text-red-600 dark:text-red-400' : ''} />
        <Stat label="Momentum"
              value={mom == null ? '—' : Math.abs(mom) < 0.005 ? 'flat' : `${mom > 0 ? '+' : ''}${mom.toFixed(2)}`}
              tone={mom == null || Math.abs(mom) < 0.005 ? 'text-gray-400'
                : mom > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'} />
        <Stat label="Mentions" value={num(c.mentions) == null ? '—' : num(c.mentions)} />
        <Stat label="Stance" value={`${bull}·${bear}·${neutral}`} />
      </div>
      <div className="mt-2 text-[10px] text-gray-400">
        Stance is bull · bear · neutral across {bull + bear + neutral} observations.
        {c.coverage ? ` Coverage: ${String(c.coverage).replace(/_/g, ' ')}.` : ''}
      </div>
    </div>
  );
}
